/**
 * 서버 카트(guest-cart · 로그인 uid 카트) 반영이 **끝났는가** — GE 바스켓 표식(`data-ge-basket-*`)의 게이트.
 *
 * ## 왜 필요한가
 *
 * GE 스크립트는 장바구니 DOM 이 바뀌면 곧바로(실측 0.2초) 우리 `get-checkout-cart-info` 로 **서버 카트**를 읽고, 그 응답의
 * 금액을 바스켓 표식이 붙은 요소에 **화면 줄 순서대로** 덮어쓴다(CartItemId 로 맞추지 않는다). 그런데 우리 서버 카트
 * 반영(`syncCart`)은 500ms debounce + 요청 시간이라 0.7초쯤 착지한다. 그 사이 GE 는 **옛 카트**를 읽어:
 *
 * ```
 *   2026-11-05 prod JP · 로그인 · 카트 [Camo×2, Pro] 에 Onyx 담기 → /cart (1.5초 시점)
 *   Onyx 줄 ¥100,400 (= 옛 첫 줄 Camo×2)   Camo 줄 ¥88,800 (= 옛 둘째 줄 Pro)   소계 ¥189,200 (= 옛 합계)
 * ```
 *
 * 제보(현우님, prod JP): Camo 1개 카트에 Onyx 를 담자 두 줄 다 ¥50,200 · 소계 ¥50,200. 같은 기전이다.
 *
 * 그래서 반영이 끝나기 전에는 표식을 떼어 둔다 — 표식이 없으면 GE 가 옛 카트를 읽어도 덮어쓸 자리가 없고, 각 가격은 개별
 * 환산(`data-ge-price`)으로 맞게 보인다. 반영이 끝나 표식이 다시 붙으면 GE 가 DOM 변화를 보고 최신 카트를 다시 읽는다
 * (`RecalculateOnDOMChange: true` — GE 머천트 설정). docs/global-e-storefront.md 「바스켓 표식 게이트」.
 *
 * ## 수명
 *
 * - **대기 시작**: `syncCart` 가 payload 를 받는 순간(debounce 시작) — `markCartSyncPending`.
 * - **진행**: debounce 가 끝나 실제 요청 체인에 올라간 동안 — `beginCartSync` ~ `endCartSync`.
 * - **끝**: 대기 payload 가 없고 진행 중인 요청도 0 일 때. 요청이 **실패해도** 끝으로 본다 — 서버가 옛 카트로 남아 있으면
 *   표식을 떼어 둔다고 나아지지 않고, 영원히 떼어 두면 GE 합계가 영영 안 나온다(종전 동작으로 떨어진다).
 *   요청이 **매달려도**(체인에 타임아웃이 없다) 링크당 `CART_SYNC_SETTLE_CAP_MS`(`src/api/CartApi.ts`) 뒤엔 끝으로 본다.
 * - **못 읽으면**(SSR · 이 모듈을 안 거친 경로): 끝으로 본다 = 종전처럼 표식을 붙인다.
 *
 * ⚠️ 추적 범위는 `CartApi` 의 `syncCart`·`flushCartSync`(= `enqueueSync` 큐)뿐이다. 같은 guest-cart 문서를 쓰는 `mergeGuestCart` ·
 * `claimGuestCartOwner`(로그인 전이) · `writeGuestCartForCheckout`(바로구매 full-nav) 은 여기를 거치지 않는다.
 */

let pending = false;
let active = 0;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

/** 대기 payload 가 생겼다/없어졌다(debounce 타이머 시작 · 타이머 만료나 flush 로 큐에 올림). */
export function markCartSyncPending(next: boolean): void {
  if (pending === next) return;
  const before = isCartSyncSettled();
  pending = next;
  if (before !== isCartSyncSettled()) emit();
}

/** 실제 요청 체인 1건 시작. 반드시 `endCartSync` 와 짝을 이룬다(성공·실패 무관). */
export function beginCartSync(): void {
  const before = isCartSyncSettled();
  active += 1;
  if (before !== isCartSyncSettled()) emit();
}

/** 실제 요청 체인 1건 끝(성공·실패 무관). */
export function endCartSync(): void {
  const before = isCartSyncSettled();
  active = Math.max(0, active - 1);
  if (before !== isCartSyncSettled()) emit();
}

/** 대기 payload 도, 진행 중인 요청도 없다. */
export function isCartSyncSettled(): boolean {
  return !pending && active === 0;
}

export function subscribeCartSyncSettled(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** 테스트 전용 — 모듈 상태 초기화. */
export function __resetCartSyncStateForTest(): void {
  pending = false;
  active = 0;
  listeners.clear();
}
